import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Collapse,
  Tooltip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { useState, useEffect, useMemo, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import LeaveReportView, { type LeaveReportEntry } from '../components/LeaveReportView';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { formatDate as formatAppDate } from '../utils/dateFormat';
import DocumentUploadDialog from '../components/DocumentUploadDialog';
import SupportingDocumentsTable from '../components/SupportingDocumentsTable';
import { isViewableContentType, openBlobInNewTab } from '../utils/documentViewer';
import { useAuth } from '../contexts/AuthContext';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad } from '../utils/userFacingCopy';

interface LeaveReportResponse {
  entries: LeaveReportEntry[];
}

interface LeaveRequestDto {
  id: string;
  requesterId?: string;
  requesterName?: string;
  leaveTypeName?: string;
  leaveTypeCode?: string;
  fromDate: string;
  toDate: string;
  requestedDays: number;
  status: string;
  wouldBeAdvancedLeave?: boolean;
  wouldExceedCycleEntitlement?: boolean;
  entitlementCapDays?: number | null;
  entitlementPeriodDescription?: string | null;
  usedInPeriodDays?: number;
  wouldExceedCap?: boolean;
  studyLeaveInsufficientTenure?: boolean;
  studyLeavePolicyReminder?: string | null;
  evidenceRequired?: boolean;
  evidenceRequiredReason?: string | null;
  sickLeaveMonFriOrAdjacentToHolidayMessage?: string | null;
}

interface LeaveRequestEvidenceDto {
  id: string;
  fileName: string;
  contentType?: string;
  fileSize: number;
  uploadedByName: string;
  uploadedAt: string;
  description?: string;
}

interface StatutoryAnnualLeaveStatusEntry {
  employeeId: string;
  employeeName: string;
  employeeNumber?: number;
  departmentName?: string;
  businessUnitName?: string;
  periodYear: number;
  annualDaysTaken: number;
  daysStillToTake: number;
  monthsRemaining: number;
  requiredRatePerMonth: number;
  status: 'Green' | 'Amber' | 'Red';
}

export default function TeamLeavePage() {
  const { user } = useAuth();
  const isManager = Boolean(user?.roles?.some((r: string) => r === 'Manager'));
  const [teamViewDirectReportsOnly, setTeamViewDirectReportsOnly] = useState(true);
  const [data, setData] = useState<LeaveReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [periodYear, setPeriodYear] = useState<number>(new Date().getFullYear());
  const [pendingToApprove, setPendingToApprove] = useState<LeaveRequestDto[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [approveLoadingId, setApproveLoadingId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [leaveTypeFilterName, setLeaveTypeFilterName] = useState('');
  const [chartOpen, setChartOpen] = useState(true);
  const [approveConfirmRequest, setApproveConfirmRequest] = useState<LeaveRequestDto | null>(null);
  const [approveEvidenceDocuments, setApproveEvidenceDocuments] = useState<LeaveRequestEvidenceDto[]>([]);
  const [approveEvidenceLoading, setApproveEvidenceLoading] = useState(false);
  const [approveEvidenceUploadOpen, setApproveEvidenceUploadOpen] = useState(false);
  const [approveEvidenceUploading, setApproveEvidenceUploading] = useState(false);
  const [statutoryStatus, setStatutoryStatus] = useState<StatutoryAnnualLeaveStatusEntry[]>([]);
  const [statutoryLoading, setStatutoryLoading] = useState(false);
  const [statutorySectionOpen, setStatutorySectionOpen] = useState(true);
  const [statutorySortBy, setStatutorySortBy] = useState<'employeeName' | 'departmentName' | 'annualDaysTaken' | 'daysStillToTake' | 'status'>('status');
  const [statutorySortDir, setStatutorySortDir] = useState<'asc' | 'desc'>('asc');
  const [statutoryPage, setStatutoryPage] = useState(0);
  const [statutoryRowsPerPage, setStatutoryRowsPerPage] = useState(10);
  const [statutorySearch, setStatutorySearch] = useState('');

  const loadPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const res = await apiService.get<LeaveRequestDto[]>(API_ENDPOINTS.LEAVE_REQUESTS.PENDING_FOR_MANAGER);
      setPendingToApprove(Array.isArray(res.data) ? res.data : []);
    } catch {
      setPendingToApprove([]);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const drParam = isManager && teamViewDirectReportsOnly ? '&directReportsOnly=true' : '';
      const res = await apiService.get<LeaveReportResponse>(
        `${API_ENDPOINTS.LEAVE.TEAM_REPORT}?periodYear=${periodYear}${drParam}`
      );
      setData(res.data ?? { entries: [] });
    } catch (err) {
            const message = getApiErrorMessage(err, couldNotLoad('the team leave report'));
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [periodYear, isManager, teamViewDirectReportsOnly]);

  const loadStatutory = useCallback(async () => {
    setStatutoryLoading(true);
    try {
      const res = await apiService.get<StatutoryAnnualLeaveStatusEntry[]>(
        API_ENDPOINTS.LEAVE.TEAM_STATUTORY_ANNUAL_STATUS(periodYear, isManager && teamViewDirectReportsOnly)
      );
      setStatutoryStatus(Array.isArray(res.data) ? res.data : []);
    } catch {
      setStatutoryStatus([]);
    } finally {
      setStatutoryLoading(false);
    }
  }, [periodYear, isManager, teamViewDirectReportsOnly]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    loadPending();
  }, [loadPending]);
  useEffect(() => {
    loadStatutory();
  }, [loadStatutory]);

  const handleApprove = async (requestId: string, withAdvancedLeave: boolean) => {
    setApproveConfirmRequest(null);
    setApproveLoadingId(requestId);
    try {
      await apiService.post(API_ENDPOINTS.LEAVE_REQUESTS.APPROVE(requestId), {
        approveWithAdvancedLeave: withAdvancedLeave,
      });
      await loadPending();
      await load();
    } catch {
      // ignore
    } finally {
      setApproveLoadingId(null);
    }
  };

  const loadApprovalEvidence = useCallback(async (requestId: string) => {
    setApproveEvidenceLoading(true);
    try {
      const res = await apiService.get<LeaveRequestEvidenceDto[]>(API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE(requestId));
      setApproveEvidenceDocuments(Array.isArray(res.data) ? res.data : []);
    } catch {
      setApproveEvidenceDocuments([]);
    } finally {
      setApproveEvidenceLoading(false);
    }
  }, []);

  const handleApproveClick = (r: LeaveRequestDto) => {
    setApproveConfirmRequest(r);
    loadApprovalEvidence(r.id);
  };

  const handleApprovalEvidenceUpload = async (files: File[], description: string) => {
    if (!approveConfirmRequest?.id) return;
    setApproveEvidenceUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        if (description.trim()) formData.append('description', description.trim());
        await apiService.post(API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE_UPLOAD(approveConfirmRequest.id), formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      await loadApprovalEvidence(approveConfirmRequest.id);
      setApproveEvidenceUploadOpen(false);
    } catch {
      // ignore
    } finally {
      setApproveEvidenceUploading(false);
    }
  };

  const handleApprovalEvidenceView = async (documentId: string, fileName: string, contentType: string) => {
    if (!approveConfirmRequest?.id) return;
    try {
      const response = await apiService.get(
        API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE_DOWNLOAD(approveConfirmRequest.id, documentId),
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      if (isViewableContentType(contentType)) {
        openBlobInNewTab(blob, contentType);
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {
      // ignore
    }
  };

  const handleApprovalEvidenceDownload = async (documentId: string, fileName: string) => {
    if (!approveConfirmRequest?.id) return;
    try {
      const response = await apiService.get(
        API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE_DOWNLOAD(approveConfirmRequest.id, documentId),
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  };

  const handleApprovalEvidenceDelete = async (documentId: string) => {
    if (!approveConfirmRequest?.id) return;
    try {
      await apiService.delete(API_ENDPOINTS.LEAVE_REQUESTS.EVIDENCE_DELETE(approveConfirmRequest.id, documentId));
      await loadApprovalEvidence(approveConfirmRequest.id);
    } catch {
      // ignore
    }
  };

  const statutoryFiltered = useMemo(() => {
    let list = statutoryStatus;
    if (statutorySearch.trim()) {
      const q = statutorySearch.toLowerCase();
      list = list.filter(
        (e) =>
          (e.employeeName ?? '').toLowerCase().includes(q) ||
          (e.departmentName ?? '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [statutoryStatus, statutorySearch]);

  const statutorySorted = useMemo(() => {
    const a = [...statutoryFiltered];
    const cmp = (x: StatutoryAnnualLeaveStatusEntry, y: StatutoryAnnualLeaveStatusEntry) => {
      let v: number | string;
      if (statutorySortBy === 'employeeName') v = (x.employeeName ?? '').localeCompare(y.employeeName ?? '');
      else if (statutorySortBy === 'departmentName') v = (x.departmentName ?? '').localeCompare(y.departmentName ?? '');
      else if (statutorySortBy === 'annualDaysTaken') v = x.annualDaysTaken - y.annualDaysTaken;
      else if (statutorySortBy === 'daysStillToTake') v = x.daysStillToTake - y.daysStillToTake;
      else v = (x.status === 'Red' ? 0 : x.status === 'Amber' ? 1 : 2) - (y.status === 'Red' ? 0 : y.status === 'Amber' ? 1 : 2);
      return v as number;
    };
    a.sort((x, y) => (statutorySortDir === 'asc' ? cmp(x, y) : -cmp(x, y)));
    return a;
  }, [statutoryFiltered, statutorySortBy, statutorySortDir]);

  const statutoryPaginated = useMemo(
    () => statutorySorted.slice(statutoryPage * statutoryRowsPerPage, statutoryPage * statutoryRowsPerPage + statutoryRowsPerPage),
    [statutorySorted, statutoryPage, statutoryRowsPerPage]
  );

  const handleStatutorySort = (field: typeof statutorySortBy) => {
    if (statutorySortBy === field) setStatutorySortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setStatutorySortBy(field);
      setStatutorySortDir('asc');
    }
    setStatutoryPage(0);
  };

  const getStatutorySortIcon = (field: typeof statutorySortBy) => {
    if (statutorySortBy !== field) return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    return statutorySortDir === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  return (
    <>
    <AppLayout>
      <LeaveReportView
        title="Team Leave"
        icon={<EventAvailableIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />}
        additionalFilterControls={
          isManager ? (
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="team-leave-scope-label">Team</InputLabel>
              <Select
                labelId="team-leave-scope-label"
                label="Team"
                value={teamViewDirectReportsOnly ? 'direct' : 'hierarchy'}
                onChange={(e) => {
                  setTeamViewDirectReportsOnly(e.target.value === 'direct');
                  setStatutoryPage(0);
                }}
              >
                <MenuItem value="direct">Direct reports only</MenuItem>
                <MenuItem value="hierarchy">Everyone in reporting line</MenuItem>
              </Select>
            </FormControl>
          ) : undefined
        }
        entries={data?.entries ?? []}
        loading={loading}
        error={error}
        onClearError={() => setError('')}
        periodYear={periodYear}
        onPeriodYearChange={setPeriodYear}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        leaveTypeFilterName={leaveTypeFilterName}
        onLeaveTypeFilterNameChange={setLeaveTypeFilterName}
        chartOpen={chartOpen}
        onChartOpenChange={setChartOpen}
        emptyMessage="No leave data for your team for this period."
        tableEmptyMessage="No results match your search or filters."
        belowHeader={
          <Alert severity="info" sx={{ mb: 3 }}>
            This page shows your team&apos;s leave (balance and approved requests) for the selected period.
            {isManager &&
              ' Defaults to direct reports (same scope as approvals). Use the Team filter to include everyone in your reporting line.'}
          </Alert>
        }
        topSections={
          <>
            {(pendingToApprove.length > 0 || pendingLoading) && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Pending requests to approve
                  </Typography>
                  {pendingLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Employee</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>From</TableCell>
                            <TableCell>To</TableCell>
                            <TableCell>Days</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pendingToApprove.map((r) => (
                            <TableRow key={r.id} hover>
                              <TableCell>{r.requesterName}</TableCell>
                              <TableCell>{r.leaveTypeName ?? r.leaveTypeCode}</TableCell>
                              <TableCell>{formatAppDate(r.fromDate)}</TableCell>
                              <TableCell>{formatAppDate(r.toDate)}</TableCell>
                              <TableCell>{r.requestedDays}</TableCell>
                              <TableCell>
                                {r.status === 'PendingSeniorApproval' ? (
                                  <Chip size="small" label="Pending senior approval" color="warning" variant="outlined" />
                                ) : (
                                  <Chip size="small" label="Pending approval" variant="outlined" />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleApproveClick(r)}
                                  disabled={approveLoadingId === r.id}
                                >
                                  {approveLoadingId === r.id ? <CircularProgress size={16} /> : r.status === 'PendingSeniorApproval' ? 'Approve (senior)' : 'Approve'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        }
        extraSections={
          <>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={() => setStatutorySectionOpen((o) => !o)}
                >
                  <Typography variant="subtitle1">
                    Statutory annual leave (country target)
                  </Typography>
                  <IconButton size="small" aria-label={statutorySectionOpen ? 'Collapse' : 'Expand'}>
                    {statutorySectionOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
                <Collapse in={statutorySectionOpen}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                    RAG status is time-aware: risk increases when many days remain to take and the end of the fiscal year is near.
                  </Typography>
                  {statutoryLoading ? (
                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <>
                      <TextField
                        size="small"
                        placeholder="Search by name or department..."
                        value={statutorySearch}
                        onChange={(e) => { setStatutorySearch(e.target.value); setStatutoryPage(0); }}
                        sx={{ mb: 2, maxWidth: 320 }}
                        fullWidth
                      />
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleStatutorySort('employeeName')}>
                                  <strong>Employee</strong>
                                  {getStatutorySortIcon('employeeName')}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleStatutorySort('departmentName')}>
                                  <strong>Department</strong>
                                  {getStatutorySortIcon('departmentName')}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', justifyContent: 'flex-end', '&:hover': { opacity: 0.7 } }} onClick={() => handleStatutorySort('annualDaysTaken')}>
                                  <strong>Days taken</strong>
                                  {getStatutorySortIcon('annualDaysTaken')}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', justifyContent: 'flex-end', '&:hover': { opacity: 0.7 } }} onClick={() => handleStatutorySort('daysStillToTake')}>
                                  <strong>Days to take</strong>
                                  {getStatutorySortIcon('daysStillToTake')}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', '&:hover': { opacity: 0.7 } }} onClick={() => handleStatutorySort('status')}>
                                  <strong>Status</strong>
                                  {getStatutorySortIcon('status')}
                                </Box>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {statutoryPaginated.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4, border: 'none' }}>
                                  <Typography color="text.secondary">
                                    {statutoryStatus.length === 0
                                      ? 'No team members or no statutory annual leave data for this period.'
                                      : 'No results match your search.'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              statutoryPaginated.map((row) => (
                                <TableRow key={row.employeeId} hover>
                                  <TableCell>{row.employeeName}</TableCell>
                                  <TableCell>{row.departmentName ?? '—'}</TableCell>
                                  <TableCell align="right">{row.annualDaysTaken}</TableCell>
                                  <TableCell align="right">{row.daysStillToTake}</TableCell>
                                  <TableCell>
                                    {row.status === 'Green' && (
                                      <Tooltip title="Employee has met their country-specific statutory annual leave target for this fiscal year.">
                                        <Chip size="small" label="Met" color="success" />
                                      </Tooltip>
                                    )}
                                    {row.status === 'Amber' && (
                                      <Tooltip title="Below country statutory target, but enough time remains to catch up at a reasonable rate. Encourage booking leave.">
                                        <Chip size="small" label="At risk" color="warning" />
                                      </Tooltip>
                                    )}
                                    {row.status === 'Red' && (
                                      <Tooltip title="High risk: little time left in the year and/or very high days-per-month needed to reach country target. Prioritise follow-up.">
                                        <Chip size="small" label="High risk" color="error" />
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <TablePagination
                        component="div"
                        count={statutoryFiltered.length}
                        page={statutoryPage}
                        onPageChange={(_e, newPage) => setStatutoryPage(newPage)}
                        rowsPerPage={statutoryRowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setStatutoryRowsPerPage(parseInt(e.target.value, 10));
                          setStatutoryPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                      />
                    </>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          </>
        }
      />
    </AppLayout>
    <Dialog
      open={approveConfirmRequest !== null}
      onClose={() => setApproveConfirmRequest(null)}
      aria-labelledby="approve-confirm-dialog-title"
      aria-describedby="approve-confirm-dialog-description"
    >
      <DialogTitle id="approve-confirm-dialog-title">
        {approveConfirmRequest?.status === 'PendingSeniorApproval' ? 'Senior approval' : 'Approve leave request'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="approve-confirm-dialog-description" component="div">
          {approveConfirmRequest && (
            <>
              {approveConfirmRequest.status === 'PendingSeniorApproval' ? (
                <p style={{ margin: 0 }}>The line manager has approved. Confirm your senior approval?</p>
              ) : (
                <p style={{ margin: 0 }}>Are you sure you want to approve this leave request?</p>
              )}
              {(() => {
                const isAnnual = approveConfirmRequest.leaveTypeCode === 'Annual' || approveConfirmRequest.leaveTypeName === 'Annual Leave';
                const annualDaysTaken = data?.entries
                  ? data.entries
                      .filter((e) => (e.employeeId === approveConfirmRequest.requesterId) && (e.leaveTypeName === 'Annual' || e.leaveTypeName === 'Annual Leave'))
                      .reduce((sum, e) => sum + e.daysTaken, 0)
                  : 0;
                return isAnnual && annualDaysTaken > 20 ? (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    This employee has already taken more than 20 days annual leave this year ({annualDaysTaken} days). This is for your information only; approval is not restricted.
                  </Alert>
                ) : null;
              })()}
              {approveConfirmRequest.entitlementCapDays != null && (
                <p style={{ margin: '8px 0 0 0', fontSize: '0.95rem' }}>
                  Cap: {approveConfirmRequest.entitlementCapDays} days ({approveConfirmRequest.entitlementPeriodDescription ?? 'per period'}).
                  Used: {approveConfirmRequest.usedInPeriodDays ?? 0}. This request: {approveConfirmRequest.requestedDays}.
                  {approveConfirmRequest.wouldExceedCap && (
                    <strong> Approving would exceed the cap.</strong>
                  )}
                </p>
              )}
              {approveConfirmRequest.wouldExceedCycleEntitlement && (
                <p style={{ margin: '8px 0 0 0' }}>
                  After you approve, a senior manager must also approve this request.
                </p>
              )}
              {approveConfirmRequest.studyLeavePolicyReminder && (
                <p style={{ margin: '8px 0 0 0', fontSize: '0.95rem' }}>
                  {approveConfirmRequest.studyLeavePolicyReminder}
                </p>
              )}
              {approveConfirmRequest.studyLeaveInsufficientTenure && (
                <p style={{ margin: '8px 0 0 0', fontWeight: 600 }}>
                  Employee has less than 1 year of service (Microsoft Exam Leave requires 1 year).
                </p>
              )}
              {approveConfirmRequest.sickLeaveMonFriOrAdjacentToHolidayMessage && (
                <Alert severity="info" sx={{ mt: 1.5 }}>{approveConfirmRequest.sickLeaveMonFriOrAdjacentToHolidayMessage}</Alert>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Attached evidence</Typography>
                {approveConfirmRequest.evidenceRequired && approveConfirmRequest.evidenceRequiredReason && (
                  <Alert severity="info" sx={{ mb: 1 }}>{approveConfirmRequest.evidenceRequiredReason}</Alert>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AttachFileIcon />}
                  onClick={() => setApproveEvidenceUploadOpen(true)}
                  sx={{ mb: 1, textTransform: 'none' }}
                >
                  Upload document
                </Button>
                {approveEvidenceLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <SupportingDocumentsTable
                    documents={approveEvidenceDocuments.map((d) => ({
                      id: d.id,
                      fileName: d.fileName,
                      description: d.description,
                      uploadedByName: d.uploadedByName,
                      uploadedAt: d.uploadedAt,
                      fileSize: d.fileSize,
                      contentType: d.contentType,
                    }))}
                    onView={handleApprovalEvidenceView}
                    onDownload={handleApprovalEvidenceDownload}
                    onDelete={handleApprovalEvidenceDelete}
                    canEdit={true}
                    formatDate={(date) => formatAppDate(date, { includeTime: true })}
                  />
                )}
                {approveEvidenceDocuments.length === 0 && !approveEvidenceLoading && (
                  <Typography variant="body2" color="text.secondary">
                    No documents attached.
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setApproveConfirmRequest(null)}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => approveConfirmRequest && handleApprove(approveConfirmRequest.id, approveConfirmRequest.status === 'PendingSeniorApproval' ? false : (approveConfirmRequest.wouldBeAdvancedLeave ?? false))}
          autoFocus
        >
          {approveConfirmRequest?.status === 'PendingSeniorApproval' ? 'Approve (senior)' : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
    <DocumentUploadDialog
      open={approveEvidenceUploadOpen}
      onClose={() => setApproveEvidenceUploadOpen(false)}
      onUpload={handleApprovalEvidenceUpload}
      uploading={approveEvidenceUploading}
      title="Upload leave evidence"
    />
    </>
  );
}
